import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { authFetch } from "../utils/authFetch";

const euro = (value) =>
  new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
  }).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleDateString("it-IT");
};

const colors = {
  burgundy: "#681E35",
  burgundyDark: "#411225",
  gold: "#D7B56D",
  ink: "#19191C",
  muted: "#6C6C74",
  background: "#F7F5F2",
};

const orderStatus = (order) => {
  const status = String(order.stato_ordine || "").toUpperCase();
  const appStatus = String(order.stato_app || "").toUpperCase();

  if (status === "CANCELED") return ["Annullato", "#A83242", "#FFF0F1"];
  if (status === "DRAFT") return ["Bozza", "#777777", "#F0F0F0"];
  if (order.ordine_completato || status === "FULFILLED" || appStatus === "RITIRATO")
    return ["Consegnato", "#24764B", "#E8F6EE"];
  if (appStatus === "PRONTO_RITIRO") return ["Pronto", "#24764B", "#E8F6EE"];
  if (appStatus === "IN_PREPARAZIONE") return ["In preparazione", "#8B5A13", "#FFF3DC"];
  if (appStatus === "EVASO") return ["Evaso", "#24764B", "#E8F6EE"];
  if (status === "APPROVED") return ["Confermato", "#265C9A", "#EAF2FC"];
  if (status === "SUBMITTED" || appStatus === "INVIATO")
    return ["Inviato", "#265C9A", "#EAF2FC"];
  return [order.stato_app || order.stato_ordine || "Da verificare", "#777777", "#F0F0F0"];
};

function Icon({ name, size = 22 }) {
  const paths = {
    building: <><rect x="4" y="3" width="16" height="18" rx="2" /><path d="M9 21v-4h6v4M8 7h1M15 7h1M8 11h1M15 11h1" /></>,
    orders: <><rect x="5" y="4" width="14" height="17" rx="2" /><path d="M9 4V2h6v2M9 10h6M9 14h6M9 18h4" /></>,
    wallet: <><rect x="3" y="6" width="18" height="15" rx="3" /><path d="M3 10h18M6 6V4h12" /><circle cx="17" cy="15" r="1" /></>,
    clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
    plus: <><circle cx="12" cy="12" r="9" /><path d="M12 7v10M7 12h10" /></>,
    catalog: <><path d="M4 4h7v16H4zM13 4h7v16h-7zM7 8h1M16 8h1" /></>,
    calendar: <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M7 3v4M17 3v4M3 10h18M8 15l2 2 5-5" /></>,
    arrow: <><path d="M5 12h14m-6-6 6 6-6 6" /></>,
    search: <><circle cx="10.5" cy="10.5" r="6.5" /><path d="m16 16 5 5" /></>,
    logout: <><path d="M10 4H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h5M15 16l4-4-4-4M8 12h11" /></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
      strokeLinejoin="round" aria-hidden="true">
      {paths[name] || paths.arrow}
    </svg>
  );
}

function ActionCard({ title, description, icon, onClick }) {
  return (
    <button type="button" onClick={onClick}
      className="w-100 text-start d-flex align-items-center gap-3 p-3"
      style={{
        border: "1px solid #ECE6E5", borderRadius: 17,
        background: "#FFFFFF", cursor: "pointer",
      }}>
      <span style={{
        width: 48, height: 48, borderRadius: 14, flexShrink: 0,
        display: "grid", placeItems: "center",
        background: "#F6EDF0", color: colors.burgundy,
      }}>
        <Icon name={icon} />
      </span>
      <span className="flex-grow-1" style={{ minWidth: 0 }}>
        <span className="d-block fw-bold" style={{ color: colors.ink }}>{title}</span>
        <span className="d-block small" style={{ color: colors.muted }}>{description}</span>
      </span>
      <span style={{ color: colors.burgundy }}><Icon name="arrow" size={19} /></span>
    </button>
  );
}

export default function AppAgenteHomePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const agenteMessage = location.state?.agenteMessage || "";
  const username = localStorage.getItem("me.username") || "";

  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const loadDashboard = async () => {
      try {
        setLoading(true);
        setError("");
        const res = await authFetch("/api/agente/dashboard/");
        const data = await res.json();
        if (!res.ok) throw new Error(data?.detail || "Impossibile caricare i dati dell'agente.");
        if (!cancelled) setDashboard(data);
      } catch (err) {
        if (!cancelled) setError(err?.message || "Errore durante il caricamento.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadDashboard();
    return () => { cancelled = true; };
  }, []);

  const logout = () => {
    ["access", "refresh", "me.username", "me.role", "me.store_name", "session_only"]
      .forEach((key) => localStorage.removeItem(key));
    ["access", "refresh"].forEach((key) => sessionStorage.removeItem(key));
    window.location.replace("/app");
  };

  const portafoglio = dashboard?.portafoglio || {};
  const aziende = Array.isArray(portafoglio.aziende) ? portafoglio.aziende : [];
  const orders = useMemo(
    () => Array.isArray(dashboard?.storico_ordini) ? dashboard.storico_ordini : [],
    [dashboard]
  );
  const payments = dashboard?.riepilogo_pagamenti || {};

  const filteredOrders = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return orders;
    return orders.filter((order) =>
      String(order.id).includes(term) ||
      String(order.azienda || "").toLowerCase().includes(term)
    );
  }, [orders, search]);

  const visibleOrders = showAll ? filteredOrders : filteredOrders.slice(0, 5);

  const stats = [
    { label: "Aziende", value: portafoglio.totale_attuali ?? 0, icon: "building" },
    { label: "Ordini", value: orders.length, icon: "orders" },
    { label: "Provvigioni maturate", value: euro(payments.provvigioni_maturate), icon: "wallet" },
    { label: "Ordini da incassare", value: payments.ordini_da_incassare ?? 0, icon: "clock" },
  ];

  return (
    <div style={{ minHeight: "100vh", background: colors.background }}>
      <div style={{
        background: `linear-gradient(135deg, ${colors.ink}, ${colors.burgundyDark} 68%, ${colors.burgundy})`,
        color: "#FFFFFF", padding: "28px 18px 44px",
      }}>
        <div className="mx-auto" style={{ maxWidth: 800 }}>
          <div className="d-flex align-items-center gap-3">
            <div style={{
              width: 54, height: 54, border: `1px solid ${colors.gold}`,
              borderRadius: 16, display: "grid", placeItems: "center",
              color: colors.gold, fontSize: 29,
            }}>✦</div>
            <div>
              <div className="small fw-semibold text-uppercase"
                style={{ color: colors.gold, letterSpacing: 2 }}>Area commerciale</div>
              <h2 className="fw-bold mb-0">Dallo Zio Roby</h2>
            </div>
          </div>
          <p className="mb-0 mt-3" style={{ color: "#E5D5DA" }}>
            {username ? `Benvenuto, ${username}` : "Benvenuto nella tua area agente"}
          </p>
          <div className="mt-3" style={{ height: 2, width: 80, background: colors.gold }} />
        </div>
      </div>

      <div className="mx-auto px-3 pb-5" style={{ maxWidth: 800, marginTop: -23 }}>
        {agenteMessage && <div className="alert alert-success rounded-4">{agenteMessage}</div>}
        {error && <div className="alert alert-danger rounded-4">{error}</div>}

        <section style={{
          background: "#FFFFFF", borderRadius: 20, padding: 20,
          boxShadow: "0 8px 26px rgba(39,20,27,.09)",
        }}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="fw-bold mb-0" style={{ color: colors.ink }}>La tua attività</h5>
            {loading && <div className="spinner-border spinner-border-sm" role="status" />}
          </div>
          <div className="row g-2">
            {stats.map((stat) => (
              <div className="col-6" key={stat.label}>
                <div className="h-100 p-3" style={{
                  background: "#F8F4F2", borderRadius: 15,
                  border: "1px solid #EFE7E4",
                }}>
                  <span style={{ color: colors.burgundy }}><Icon name={stat.icon} size={20} /></span>
                  <div className="fw-bold mt-2" style={{ fontSize: 21, color: colors.ink }}>
                    {loading ? "—" : stat.value}
                  </div>
                  <div className="small" style={{ color: colors.muted }}>{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="d-flex flex-wrap gap-3 mt-3 small">
            <span>Liquidabili: <strong>{euro(payments.provvigioni_liquidabili)}</strong></span>
            <span>Liquidate: <strong>{euro(payments.provvigioni_liquidate)}</strong></span>
            <span>Sospese: <strong>{euro(payments.provvigioni_sospese)}</strong></span>
          </div>
        </section>

        <section className="mt-4">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h5 className="fw-bold mb-0" style={{ color: colors.ink }}>Storico ordini</h5>
            <span className="small" style={{ color: colors.muted }}>
              {filteredOrders.length} ordini
            </span>
          </div>
          <div className="d-flex align-items-center gap-2 px-3 mb-3"
            style={{ background: "#FFFFFF", borderRadius: 13, border: "1px solid #E8E1DF" }}>
            <span style={{ color: colors.muted }}><Icon name="search" size={19} /></span>
            <input type="search" value={search} onChange={(e) => {
              setSearch(e.target.value); setShowAll(false);
            }} placeholder="Cerca numero ordine o azienda"
              aria-label="Cerca ordini"
              className="form-control border-0 shadow-none py-3" />
          </div>
          {loading ? (
            <div className="text-center py-4">Caricamento ordini...</div>
          ) : visibleOrders.length === 0 ? (
            <div className="p-4 text-center" style={{
              background: "#FFFFFF", borderRadius: 16, color: colors.muted,
            }}>
              {search ? "Nessun ordine corrisponde alla ricerca." : "Non ci sono ancora ordini."}
            </div>
          ) : (
            <div className="d-flex flex-column gap-3">
              {visibleOrders.map((order) => {
                const [label, foreground, background] = orderStatus(order);
                const paymentLabel = order.azienda_pagato
                  ? "Pagato"
                  : order.stato_ordine === "DRAFT"
                    ? "Da confermare"
                    : "Da incassare";
                return (
                  <button key={order.id} type="button"
                    className="w-100 text-start p-3"
                    onClick={() => navigate(`/app/agente/ordini/${order.id}/dettaglio`)}
                    style={{
                      background: "#FFFFFF", border: "1px solid #E9E1DF",
                      borderRadius: 17, boxShadow: "0 3px 12px rgba(0,0,0,.035)",
                    }}>
                    <div className="d-flex justify-content-between align-items-start gap-2">
                      <div>
                        <div className="fw-bold" style={{ color: colors.ink }}>
                          Ordine #{order.id}
                        </div>
                        <div className="small" style={{ color: colors.muted }}>
                          {formatDate(order.date)}
                        </div>
                      </div>
                      <span className="small fw-semibold px-2 py-1"
                        style={{ color: foreground, background, borderRadius: 9 }}>
                        {label}
                      </span>
                    </div>
                    <div className="fw-semibold mt-2" style={{ color: colors.burgundy }}>
                      {order.azienda || "Azienda non indicata"}
                    </div>
                    <div className="d-flex flex-wrap justify-content-between align-items-end gap-2 mt-3">
                      <div className="small" style={{ color: colors.muted }}>
                        <div>Pagamento: <strong style={{ color: colors.ink }}>
                          {order.metodo_pagamento || "Non indicato"}
                        </strong></div>
                        <div>Incasso: <strong style={{
                          color: order.azienda_pagato ? "#24764B" : "#9A641A",
                        }}>{paymentLabel}</strong></div>
                      </div>
                      <div className="text-end">
                        <div className="fw-bold" style={{ color: colors.ink }}>
                          {euro(order.totale)}
                        </div>
                        <span className="small fw-semibold" style={{ color: colors.burgundy }}>
                          Apri dettaglio ›
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          {filteredOrders.length > 5 && (
            <button type="button" className="btn w-100 mt-3 fw-semibold"
              style={{
                border: `1px solid ${colors.burgundy}`, borderRadius: 13,
                color: colors.burgundy, padding: 12,
              }} onClick={() => setShowAll(!showAll)}>
              {showAll ? "Mostra meno" : `Mostra tutti gli ordini (${filteredOrders.length})`}
            </button>
          )}
        </section>

        <section className="mt-4">
          <h5 className="fw-bold mb-3" style={{ color: colors.ink }}>Strumenti commerciali</h5>
          <div className="d-flex flex-column gap-2">
            <ActionCard icon="plus" title="Acquisisci nuova azienda"
              description="Registra una nuova azienda nel tuo portafoglio"
              onClick={() => navigate("/app/agente/acquisisci")} />
            <ActionCard icon="catalog" title="Catalogo aziende"
              description="Consulta prodotti e listino B2B"
              onClick={() => navigate("/app/agente/catalogo")} />
            <ActionCard icon="calendar" title="Promozioni aziende"
              description="Scopri offerte, sconti e omaggi riservati alle aziende"
              onClick={() => navigate("/app/agente/promozioni")} />
            <ActionCard icon="calendar" title="Promemoria commerciali"
              description="Appuntamenti e aziende da ricontattare"
              onClick={() => navigate("/app/agente/promemoria")} />
            <ActionCard icon="wallet" title="Vendite e provvigioni"
              description="Consulta liquidazioni e storico provvigioni"
              onClick={() => navigate("/app/agente/provvigioni")} />
          </div>
        </section>

        <section className="mt-4">
          <h5 className="fw-bold mb-3" style={{ color: colors.ink }}>
            Le tue aziende <span className="small fw-normal" style={{ color: colors.muted }}>
              ({aziende.length})
            </span>
          </h5>
          {aziende.length === 0 && !loading ? (
            <div className="p-4" style={{ background: "#FFFFFF", borderRadius: 16 }}>
              Nessuna azienda attualmente nel tuo portafoglio.
            </div>
          ) : (
            <div className="d-flex flex-column gap-2">
              {aziende.map((azienda) => (
                <div key={azienda.id} className="p-3"
                  style={{ background: "#FFFFFF", borderRadius: 16, border: "1px solid #ECE6E5" }}>
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <div>
                      <div className="fw-bold" style={{ color: colors.ink }}>
                        {azienda.name || "Azienda"}
                      </div>
                      <div className="small" style={{ color: colors.muted }}>{azienda.code}</div>
                    </div>
                    <span className="badge" style={{
                      background: azienda.tipo_portafoglio === "ACQUISITA" ? "#E8F6EE" : "#EAF2FC",
                      color: azienda.tipo_portafoglio === "ACQUISITA" ? "#24764B" : "#265C9A",
                    }}>
                      {azienda.tipo_portafoglio === "ACQUISITA" ? "Acquisita" : "Assegnata"}
                    </span>
                  </div>
                  <button type="button" className="btn btn-sm mt-3 fw-semibold"
                    disabled={!azienda.code}
                    style={{
                      border: `1px solid ${colors.burgundy}`, color: colors.burgundy,
                      borderRadius: 12,
                    }}
                    onClick={() => navigate(
                      `/app/agente/aziende/${encodeURIComponent(azienda.code)}/crm`
                    )}>
                    Apri scheda commerciale
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        <button type="button" onClick={logout}
          className="btn w-100 mt-4 py-3 fw-semibold d-flex align-items-center justify-content-center gap-2"
          style={{
            border: "1px solid #D9C9CD", borderRadius: 15,
            color: colors.burgundy, background: "#FFFFFF",
          }}>
          <Icon name="logout" size={19} /> Esci dall'account
        </button>
      </div>
    </div>
  );
}
